import React, { useState, useEffect, useRef } from 'react';
import '../InvoiceDetail.css';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { Breadcrumbs } from '../components/Navigation';
import { apiFetch } from '../utils/api';
import { useAnafSubmit } from '../hooks/useAnafSubmit';
import AnafStatusButton from '../components/AnafStatusButton';
import { ExtractionDiffPanel, SubmissionAuditPanel } from '../components/shared/InvoiceAuditPanels';

// Helper function to convert number to Romanian words (for chitanta)
// currency parameter determines the suffix: RON -> LEI/BANI, others -> currency code
const numberToWordsRo = (num, currency = 'RON') => {
  if (num === 0) return currency === 'RON' ? 'ZERO LEI' : 'ZERO ' + currency;

  const units = ['', 'UNU', 'DOI', 'TREI', 'PATRU', 'CINCI', 'SASE', 'SAPTE', 'OPT', 'NOUA'];
  const teens = ['ZECE', 'UNSPREZECE', 'DOISPREZECE', 'TREISPREZECE', 'PAISPREZECE',
                 'CINCISPREZECE', 'SAISPREZECE', 'SAPTESPREZECE', 'OPTSPREZECE', 'NOUASPREZECE'];
  const tens = ['', '', 'DOUAZECI', 'TREIZECI', 'PATRUZECI', 'CINCIZECI',
                'SAIZECI', 'SAPTEZECI', 'OPTZECI', 'NOUAZECI'];
  const hundreds = ['', 'O SUTA', 'DOUA SUTE', 'TREI SUTE', 'PATRU SUTE', 'CINCI SUTE',
                    'SASE SUTE', 'SAPTE SUTE', 'OPT SUTE', 'NOUA SUTE'];

  const convertUnder1000 = (n) => {
    if (n === 0) return '';
    if (n < 10) return units[n];
    if (n < 20) return teens[n - 10];
    if (n < 100) {
      const t = Math.floor(n / 10);
      const u = n % 10;
      return tens[t] + (u > 0 ? ' SI ' + units[u] : '');
    }
    const h = Math.floor(n / 100);
    const rest = n % 100;
    return hundreds[h] + (rest > 0 ? ' ' + convertUnder1000(rest) : '');
  };

  const intPart = Math.floor(num);
  const decPart = Math.round((num - intPart) * 100);

  let result = '';

  if (intPart >= 1000000) {
    const mil = Math.floor(intPart / 1000000);
    result += (mil === 1 ? 'UN MILION' : convertUnder1000(mil) + ' MILIOANE') + ' ';
  }

  const thousands = Math.floor((intPart % 1000000) / 1000);
  if (thousands > 0) {
    if (thousands === 1) {
      result += 'O MIE ';
    } else if (thousands === 2) {
      result += 'DOUA MII ';
    } else {
      result += convertUnder1000(thousands) + ' MII ';
    }
  }

  const remainder = intPart % 1000;
  if (remainder > 0 || intPart === 0) {
    result += convertUnder1000(remainder);
  }

  result = result.trim();
  if (!result) result = 'ZERO';

  // For RON: use LEI/BANI, for other currencies: just append currency code
  if (currency === 'RON') {
    if (decPart > 0) {
      result += ' LEI SI ' + decPart + ' BANI';
    } else {
      result += ' LEI';
    }
  } else {
    if (decPart > 0) {
      result += ' ' + currency + ' SI ' + decPart + ' CENTI';
    } else {
      result += ' ' + currency;
    }
  }

  return result;
};

// Helper function to check for unknown/empty values
const isUnknown = (value) => {
  // --- START OF MODIFICATION ---
  // 1. Check for null or undefined first.
  if (value === null || value === undefined) {
    return true;
  }

  // 2. If it's not a string (e.g., a number 0), it's a valid value.
  if (typeof value !== 'string') {
    return false;
  }

  // 3. For strings, trim whitespace and convert to lower case for consistent checking.
  const cleanedValue = value.trim().toLowerCase();

  // 4. An empty string is unknown.
  if (cleanedValue === '') {
    return true;
  }

  // 5. Check against a list of common placeholder texts.
  const placeholderTexts = [
    'de completat', 
    'completat manual', 
    'nespecificat', 
    'client nespecificat',
    'necunoscut'
  ];

  return placeholderTexts.some(placeholder => cleanedValue.includes(placeholder));
  // --- END OF MODIFICATION ---
};

// Helper to format date for display (DD-MM-YYYY)
const formatDateRo = (isoDate) => {
  if (!isoDate) return '—';
  try {
    const d = new Date(isoDate);
    if (isNaN(d.getTime())) return isoDate;
    return d.toLocaleDateString('ro-RO', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    }).replace(/\./g, '-'); // Prefer dash separator based on user request
  } catch (e) {
    return isoDate;
  }
};

// Helper function to check if currency is RON (eligible for ANAF submission)
// Non-RON currencies (USD, EUR, etc.) can only download XML, not submit to ANAF
const isRonCurrency = (currency) => {
  if (!currency) return true; // Default to RON if not specified
  const upper = currency.toUpperCase().trim();
  return upper === 'RON' || upper === 'LEI';
};


const FormField = ({ label, value, isEditing, onChange, type = "text", mono = false, required = false, error, fieldName, placeholder }) => {
  const isValueUnknown = isUnknown(value);
  const inputClass = `form-field-input ${mono ? 'form-field-input-mono' : ''} ${error || (isEditing && isValueUnknown) ? 'form-field-input-error' : ''}`;
  const labelClass = `form-field-label ${error || (isEditing && isValueUnknown) ? 'form-field-label-unknown' : ''}`;
  const valueClass = `form-field-value ${mono ? 'form-field-value-mono' : ''} ${isValueUnknown ? 'form-field-value-unknown' : ''}`;
  
  return (
    <div className="form-field">
      <label className={labelClass}>
        {label} {required && '*'}
      </label>
      {isEditing ? (
        <>
          <input
            type={type}
            className={inputClass}
            value={value || ''}
            onChange={(e) => onChange(fieldName, e.target.value)}
            placeholder={placeholder}
          />
          {error && <div className="form-field-error">{error}</div>}
        </>
      ) : (
        <div className={valueClass}>
          {isValueUnknown ? 'Date lipsă' : value}
        </div>
      )}
    </div>
  );
};

// Professional editable party details component
const PartyDetails = ({
  title,
  party,
  type,
  isEditing,
  onChange,
  errors,
  // ANAF lookup props (only used for buyer)
  onAnafLookup,
  anafLookupLoading,
  anafLookupError,
  isCnpValue
}) => {
  const badgeClass = type === 'seller' ? 'party-badge party-badge-seller' : 'party-badge party-badge-buyer';
  const badgeText = type === 'seller' ? 'Emitent' : 'Beneficiar';

  const handleChange = (field, value) => {
    onChange(type, field, value);
  };

  return (
    <div className={`party-card ${isEditing ? 'section-card-editing' : ''}`}>
      <div className="party-header">
        <h3 className="party-title">{title}</h3>
        <span className={badgeClass}>{badgeText}</span>
      </div>
      
      <div className="party-details">
        <FormField
          label="Denumire Societate"
          value={party?.nume}
          isEditing={isEditing}
          onChange={handleChange}
          fieldName="nume"
          required
          error={errors?.[`${type}_nume`]}
          placeholder="Introduceți denumirea societății"
        />
        
      {type === 'buyer' && party?.cnp ? (
          <FormField
            label="CNP"
            value={party?.cnp}
            isEditing={isEditing}
            onChange={handleChange}
            fieldName="cnp" // Use 'cnp' field for editing
            type="text"
            mono
            placeholder="CNP-ul persoanei fizice"
          />
        ) : (
          <>
            <FormField
              label="Cod Unic de Înregistrare (CUI)"
              value={party?.cui}
              isEditing={isEditing}
              onChange={handleChange}
              fieldName="cui"
              type="text"
              mono
              error={errors?.[`${type}_cui`]}
              placeholder="Ex: RO12345678"
            />
            {isEditing && type === 'buyer' && onAnafLookup && (
              <div className="anaf-lookup-section">
                <button
                  type="button"
                  onClick={onAnafLookup}
                  disabled={anafLookupLoading || !party?.cui?.trim() || isCnpValue}
                  className={`anaf-lookup-btn ${anafLookupLoading ? 'loading' : ''}`}
                  title={isCnpValue ? 'Căutarea ANAF nu este disponibilă pentru persoane fizice (CNP)' : 'Caută datele firmei în registrul ANAF'}
                >
                  {anafLookupLoading ? 'Se caută...' : 'Caută în ANAF'}
                </button>
                {anafLookupError && (
                  <div className="anaf-lookup-error">{anafLookupError}</div>
                )}
              </div>
            )}
          </>
        )} 
        
        {/* COMMENTED OUT: Email field for seller
        {type === 'seller' && (
          <FormField
            label="Email"
            value={party?.email}
            isEditing={isEditing}
            onChange={handleChange}
            fieldName="email"
            type="email"
            placeholder="Adresa de email"
          />
        )}
        */}
        
        <FormField
          label="Nr. Reg. Com."
          value={party?.nr_reg_com}
          isEditing={isEditing}
          onChange={handleChange}
          fieldName="nr_reg_com"
          mono
          placeholder="Ex: J40/123/2020"
        />

        {type === 'seller' && (
          <FormField
            label="IBAN"
            value={party?.iban}
            isEditing={isEditing}
            onChange={handleChange}
            fieldName="iban"
            mono
            placeholder="RO00XXXX0000000000000000"
          />
        )}

        {type === 'seller' && (
          <FormField
            label="Nume Bancă"
            value={party?.numeBanca}
            isEditing={isEditing}
            onChange={handleChange}
            fieldName="numeBanca"
            placeholder="Ex: Banca Transilvania"
          />
        )}

        <FormField
          label="Adresă (stradă, nr.)"
          value={party?.adresa}
          isEditing={isEditing}
          onChange={handleChange}
          fieldName="adresa"
          placeholder="Strada, Numărul"
        />

        <FormField
          label="Oraș"
          value={party?.oras}
          isEditing={isEditing}
          onChange={handleChange}
          fieldName="oras"
          placeholder="Oraș"
        />
        
        <FormField
          label="Județ"
          value={party?.judet}
          isEditing={isEditing}
          onChange={handleChange}
          fieldName="judet"
          placeholder="Județ"
        />
        
        <FormField
          label="Țara"
          value={party?.tara}
          isEditing={isEditing}
          onChange={handleChange}
          fieldName="tara"
          placeholder="RO"
        />
      </div>
    </div>
  );
};

// Professional editable invoice lines table
const InvoiceLinesTable = ({ lines = [], isEditing, onChange, errors, sellerCui = '', clientCategorieTVA = null, clientTvaPercent = 19 }) => {
  // Helper function to determine VAT category fallback based on CUI
  const getVatCategoryFallback = () => {
    // If database has categorieTVA, use it
    if (clientCategorieTVA) {
      return clientCategorieTVA;
    }
    // Fallback: Use old RO prefix logic
    const vatRegistered = sellerCui.startsWith('RO');
    return vatRegistered ? 'S' : 'O';
  };

  // Get the determined VAT category once
  const determinedVatCategory = getVatCategoryFallback();

  const createEmptyLine = () => ({
    'BT-153': '',
    'BT-129': 1,
    'BT-130': 'H87',
    'BT-146': 0,
    'BT-152': clientTvaPercent,
    'BT-151': determinedVatCategory,
  });

  const handleLineChange = (index, field, value) => {
    const updatedLines = [...lines];
    updatedLines[index] = {
      ...updatedLines[index],
      [field]: value,
    };
    onChange('linii_factura', updatedLines);
  };

  const handleAddLine = () => {
    onChange('linii_factura', [...lines, createEmptyLine()]);
  };

  const handleRemoveLine = (index) => {
    const updatedLines = lines.filter((_, i) => i !== index);
    onChange('linii_factura', updatedLines);
  };

  if (!lines.length) {
    return (
      <div className={`section-card ${isEditing ? 'section-card-editing' : ''}`}>
        <div className="section-header">
          <h2 className="section-title">Linii de Factură</h2>
          {isEditing && (
            <button type="button" className="review-btn" onClick={handleAddLine}>
              Adaugă linie
            </button>
          )}
        </div>
        <div className="empty-state">
          <p>Nu există linii de factură disponibile.</p>
          {isEditing && (
            <button type="button" className="review-btn" onClick={handleAddLine}>
              Creează prima linie
            </button>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className={`section-card ${isEditing ? 'section-card-editing' : ''}`}>
      <div className="section-header">
        <h2 className="section-title">Linii de Factură</h2>
        <div className="section-actions">
          <p className="section-subtitle">
            {`Detalii produse/servicii (${lines.length})`}
            {isEditing && ' – editați sau adăugați rânduri după necesitate'}
          </p>
          {isEditing && (
            <button type="button" className="review-btn" onClick={handleAddLine}>
              Adaugă linie
            </button>
          )}
        </div>
      </div>

      <div className="lines-table-container">
        <table className="lines-table">
          <thead className="lines-table-head">
            <tr>
              <th>Denumire Produs/Serviciu *</th>
              <th className="text-right">Cantitate *</th>
              <th>U.M.</th>
              <th className="text-right">Preț Unitar Net *</th>
              <th className="text-right">Valoare Net</th>
              <th className="text-center">TVA %</th>
              <th>Categorie TVA</th>
              {isEditing && <th className="text-center">Acțiuni</th>}
            </tr>
          </thead>
          <tbody className="lines-table-body">
            {lines.map((line, index) => {
              const qty = parseFloat(line['BT-129']) || 0;
              const price = parseFloat(line['BT-146']) || 0;
              const netValue = qty * price;

              return (
                <tr key={index}>
                  <td className={isEditing ? 'editing' : ''}>
                    {isEditing ? (
                      <>
                        <textarea
                          className="lines-table-textarea"
                          value={line['BT-153'] || ''}
                          onChange={(e) => handleLineChange(index, 'BT-153', e.target.value)}
                          placeholder="Denumire produs/serviciu"
                          maxLength={600}
                        />
                        <div style={{ fontSize: '0.72rem', opacity: 0.65, textAlign: 'right', marginTop: '2px' }}>
                          {(line['BT-153'] || '').length}/600
                        </div>
                      </>
                    ) : (
                      <div className="lines-table-product">
                        {isUnknown(line['BT-153']) ? <span className="text-unknown">Date lipsă</span> : line['BT-153']}
                      </div>
                    )}
                  </td>
                  <td className={isEditing ? 'editing' : ''}>
                    {isEditing ? (
                      <input
                        type="number"
                        min="0"
                        step="0.01"
                        className="lines-table-input lines-table-input-mono"
                        value={line['BT-129'] ?? 0}
                        onChange={(e) => handleLineChange(index, 'BT-129', parseFloat(e.target.value) || 0)}
                      />
                    ) : (
                      <div className="lines-table-number">
                        {qty.toLocaleString('ro-RO', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                      </div>
                    )}
                  </td>
                  <td className={isEditing ? 'editing' : ''}>
                    {isEditing ? (
                      <input
                        type="text"
                        className="lines-table-input"
                        value={line['BT-130'] || 'H87'}
                        onChange={(e) => handleLineChange(index, 'BT-130', e.target.value)}
                        placeholder="BUC"
                      />
                    ) : (
                      <div className="lines-table-unit">
                        {isUnknown(line['BT-130']) ? <span className="text-unknown">Date lipsă</span> : (line['BT-130'] === 'H87' ? 'BUC' : line['BT-130'])}
                      </div>
                    )}
                  </td>
                  <td className={isEditing ? 'editing' : ''}>
                    {isEditing ? (
                      <input
                        type="number"
                        min="0"
                        step="0.0001"
                        className="lines-table-input lines-table-input-mono"
                        value={line['BT-146'] ?? 0}
                        onChange={(e) => handleLineChange(index, 'BT-146', parseFloat(e.target.value) || 0)}
                      />
                    ) : (
                      <div className="lines-table-number">
                        {price.toLocaleString('ro-RO', {
                          minimumFractionDigits: 2,
                          maximumFractionDigits: 2,
                        })}
                      </div>
                    )}
                  </td>
                  <td>
                    <div className="lines-table-amount">
                      {netValue.toLocaleString('ro-RO', {
                        minimumFractionDigits: 2,
                        maximumFractionDigits: 2,
                      })}
                    </div>
                  </td>
                  <td className={isEditing ? 'editing' : ''}>
                    {isEditing ? (
                      <input
                        type="number"
                        min="0"
                        max="100"
                        className="lines-table-input"
                        value={line['BT-152'] ?? 0}
                        onChange={(e) => {
                          const parsed = parseInt(e.target.value, 10);
                          handleLineChange(index, 'BT-152', Number.isNaN(parsed) ? 0 : parsed);
                        }}
                      />
                    ) : (
                      <div className="text-center">
                        <span className="tax-rate-badge">{Number(line['BT-152'] ?? 0)}%</span>
                      </div>
                    )}
                  </td>
                  <td className={isEditing ? 'editing' : ''}>
                    {isEditing ? (
                      <div className="lines-table-input" style={{ padding: '0.5rem', backgroundColor: '#eae7de', color: '#746c5a', textAlign: 'center' }}>
                        {determinedVatCategory}
                        <div style={{ fontSize: '0.75rem', marginTop: '0.25rem' }}>
                          Auto din ANAF
                        </div>
                      </div>
                    ) : (
                      <span className={determinedVatCategory === 'S' ? 'tax-badge tax-badge-standard' : 'tax-badge tax-badge-other'}>
                        {determinedVatCategory || 'N/A'}
                      </span>
                    )}
                  </td>
                  {isEditing && (
                    <td className="text-center">
                      <button
                        type="button"
                        className="lines-table-remove"
                        onClick={() => handleRemoveLine(index)}
                        disabled={lines.length === 1}
                      >
                        Șterge
                      </button>
                    </td>
                  )}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      {errors?.linii_factura && (
        <div className="form-field-error" style={{ marginTop: '0.5rem' }}>{errors.linii_factura}</div>
      )}
    </div>
  );
};

// Professional status badge with accountant-friendly observations
const StatusBadge = ({ status, observations }) => {
  const [isExpanded, setIsExpanded] = useState(true);

  const statusConfig = {
    GREEN: {
      className: 'status-overview status-overview-green',
      iconClass: 'status-overview-icon status-overview-icon-green',
      labelClass: 'status-overview-label status-overview-label-green',
      obsClass: 'status-overview-observations status-overview-observations-green',
      label: 'Validată – pregătită pentru SPV',
      icon: '✓',
      mainMessage: 'Am considerat că am toate informațiile necesare pentru a crea această factură.'
    },
    YELLOW: {
      className: 'status-overview status-overview-yellow',
      iconClass: 'status-overview-icon status-overview-icon-yellow',
      labelClass: 'status-overview-label status-overview-label-yellow',
      obsClass: 'status-overview-observations status-overview-observations-yellow',
      label: 'Verificare necesară – completați câmpurile lipsă',
      icon: '⚠',
      mainMessage: 'Am identificat câteva informații care ar trebui verificate sau completate pentru această factură.'
    },
    RED: {
      className: 'status-overview status-overview-red',
      iconClass: 'status-overview-icon status-overview-icon-red',
      labelClass: 'status-overview-label status-overview-label-red',
      obsClass: 'status-overview-observations status-overview-observations-red',
      label: 'Informații lipsă sau nevalide',
      icon: '✗',
      mainMessage: 'Am identificat informații lipsă sau nevalide care trebuie corectate pentru această factură.'
    }
  };

  const config = statusConfig[status] || statusConfig['YELLOW'];

  const normalizeLine = (line) => {
    if (!line) return null;
    const original = line.trim();
    if (!original) return null;

    // Drop generic sources
    if ( /^Factură creată din/i.test(original)) return null;

    // Simple diacritics removal to make pattern matching robust
    const removeDiacritics = (s) => s
      .replace(/[ăâ]/gi, 'a')
      .replace(/[î]/gi, 'i')
      .replace(/[șş]/gi, 's')
      .replace(/[țţ]/gi, 't')
      .replace(/[ĂÂ]/g, 'A')
      .replace(/[Î]/g, 'I')
      .replace(/[ȘŞ]/g, 'S')
      .replace(/[ȚŢ]/g, 'T');

    const plain = removeDiacritics(original).toLowerCase();

    // Emitent (seller) messages
    if (plain.includes('vanzatorul este un client placeholder')) {
      const emailMatch = original.match(/\(([^)]+)\)/);
      const email = emailMatch ? emailMatch[1] : undefined;
      return `Emitent: client neidentificat complet${email ? ` (${email})` : ''}. Completați CUI și denumirea corecte.`;
    }
    if (plain.includes('cui vanzator') && (plain.includes('lipseste') || plain.includes('lipsește'))) {
      return 'Emitent: CUI lipsește. Completați CUI-ul firmei dvs.';
    }
    if (plain.includes('numele vanzator') && (plain.includes('placeholder') || plain.includes('lipseste') || plain.includes('lipsește'))) {
      return 'Emitent: denumirea pare provizorie. Introduceți denumirea legală completă.';
    }
    if (plain.includes('cui vanzator') && plain.includes('nu corespunde')) {
      return 'Emitent: CUI-ul nu corespunde cu firma selectată. Verificați identificarea clientului.';
    }

    // Beneficiar (buyer) messages
    if (plain.includes('cui cumparator') && (plain.includes('lipseste') || plain.includes('placeholder') || plain.includes('invalid'))) {
      return 'Beneficiar: CUI lipsește sau este invalid. Introduceți CUI-ul corect.';
    }
    if (plain.includes("cumparatorul 'client nespecificat'") && plain.includes('nu are cui')) {
      return 'Beneficiar: înlocuiți „Client Nespecificat” cu denumirea și CUI-ul reale.';
    }
    if (plain.includes('numele cumparatorului') && (plain.includes('lipseste') || plain.includes('placeholder'))) {
      return 'Beneficiar: completați denumirea companiei.';
    }
    if (plain.includes('cui cumparator') && plain.includes('aparține altei firme')) {
      return 'Beneficiar: CUI-ul indică o altă firmă. Verificați denumirea și CUI-ul.';
    }

    // Lines and totals
    const lineDescMatch = plain.match(/linia\s*(\d+).*descrierea produsului/);
    if (lineDescMatch) {
      const nr = lineDescMatch[1] || '';
      return `Linia ${nr}: completați denumirea produsului/serviciului.`;
    }
    if (plain.includes('suma totala a facturii este 0')) {
      return 'Total factură este 0. Verificați cantitățile și prețurile; totalurile se recalculează automat.';
    }

    // Generic cleanup of BT codes
    let friendly = original.replace(/\(BT-\d+\)/g, '').replace(/\s{2,}/g, ' ').trim();
    // Prefix guidance
    if (/^V[âa]nz[âa]tor:/i.test(friendly)) return friendly.replace(/^V[âa]nz[âa]tor:/i, 'Emitent:');
    if (/^Cump[ăa]r[ăa]tor:/i.test(friendly)) return friendly.replace(/^Cump[ăa]r[ăa]tor:/i, 'Beneficiar:');
    return friendly;
  };

  const formattedList = (() => {
    const seen = new Set();
    const list = (observations || '').split(/\n+/).map(normalizeLine).filter(Boolean);
    const deduped = [];
    for (const item of list) {
      if (!seen.has(item)) {
        seen.add(item);
        deduped.push(item);
      }
    }
    return deduped;
  })();

  return (
    <div className={config.className}>
      <p className={config.obsClass} style={{ marginBottom: '0.75rem', lineHeight: '1.6' }}>
        {config.mainMessage}
      </p>
      {formattedList.length > 0 && (
        <div style={{ marginTop: '0.5rem' }}>
          <button
            type="button"
            onClick={() => setIsExpanded(!isExpanded)}
            className="interpretability-toggle-btn"
            style={{
              background: 'none',
              border: 'none',
              color: 'inherit',
              textDecoration: 'underline',
              cursor: 'pointer',
              fontSize: '0.875rem',
              fontWeight: '500',
              padding: '0',
            }}
          >
            Pentru interpretabilitate, click aici {isExpanded ? '▲' : '▼'}
          </button>
          {isExpanded && (
            <ul className={config.obsClass} style={{ marginTop: '0.75rem', paddingLeft: '1.5rem' }}>
              {formattedList.map((item, idx) => (
                <li key={idx} style={{ marginBottom: '0.5rem' }}>{item}</li>
              ))}
            </ul>
          )}
        </div>
      )}
    </div>
  );
};

// Validation messages component
const ValidationMessage = ({ type, message }) => {
  const messageClass = type === 'error' 
    ? 'validation-message validation-message-error' 
    : type === 'success'
    ? 'validation-message validation-message-success'
    : 'validation-message';

  return (
    <div className={messageClass}>
      <p className="validation-message-text">{message}</p>
    </div>
  );
};

// Loading, Error states (same as before)
const LoadingState = () => (
  <div className="loading-container">
    <div className="loading-skeleton">
      <div className="skeleton-box skeleton-title"></div>
      <div className="skeleton-box skeleton-subtitle"></div>
      <div className="skeleton-box skeleton-line"></div>
      <div className="skeleton-box skeleton-line-short"></div>
    </div>
  </div>
);

const ErrorState = ({ error }) => (
  <div className="error-container">
    <div className="error-icon">
      <div className="error-icon-dot"></div>
    </div>
    <h3 className="error-title">Eroare</h3>
    <p className="error-message">{error}</p>
  </div>
);

// Custom hook for fetching invoice details
const useInvoiceDetails = (id) => {
  const [invoice, setInvoice] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchInvoiceDetails = async () => {
      setLoading(true);
      try {
        const response = await apiFetch(`/api/invoices/${id}`);
        if (!response.ok) throw new Error('Eroare la preluarea detaliilor facturii.');
        
        const data = await response.json();
        setInvoice(data);
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };
    fetchInvoiceDetails();
  }, [id]);

  return { invoice, loading, error };
};

// Main professional invoice detail component with editing
export default function InvoiceDetailPage() {
  const id = window.location.pathname.split('/').pop() || '1';
  const { invoice, loading, error } = useInvoiceDetails(id);
  const location = useLocation();
  const navigate = useNavigate();
  const invoiceContentRef = useRef(null);
  const [isEditing, setIsEditing] = useState(false);
  const [editedInvoice, setEditedInvoice] = useState(null);
  const [baseInvoice, setBaseInvoice] = useState(null);
  const [processing, setProcessing] = useState(false);
  const [validationErrors, setValidationErrors] = useState({});
  const [saveMessage, setSaveMessage] = useState(null);
  const saveMessageTimeoutRef = useRef(null);

  // Helper to show messages with auto-dismiss for success messages
  const showMessage = (message) => {
    // Clear any existing timeout
    if (saveMessageTimeoutRef.current) {
      clearTimeout(saveMessageTimeoutRef.current);
      saveMessageTimeoutRef.current = null;
    }

    setSaveMessage(message);

    // Auto-dismiss success messages after 5 seconds
    if (message && message.type === 'success') {
      saveMessageTimeoutRef.current = setTimeout(() => {
        setSaveMessage(null);
        saveMessageTimeoutRef.current = null;
      }, 5000);
    }
  };

  const [endUsers, setEndUsers] = useState([]);
  const [loadingEndUsers, setLoadingEndUsers] = useState(false);

  // Chitanta state
  const [assigningChitanta, setAssigningChitanta] = useState(false);
  const [creatingChitanta, setCreatingChitanta] = useState(false);

  // Invoice type code override (for BON_FISCAL → 751)
  const [forceStandardInvoiceCode, setForceStandardInvoiceCode] = useState(false);

  // ANAF CUI lookup state
  const [anafLookupLoading, setAnafLookupLoading] = useState(false);
  const [anafLookupError, setAnafLookupError] = useState(null);

  // Currency conversion state
  const [currencyConvertLoading, setCurrencyConvertLoading] = useState(false);
  // Editable exchange rate: pre-filled with the BNR quote when available,
  // manual entry when BNR is unavailable (feed moved twice in Aug 2026) or
  // when a contract stipulates its own rate
  const [manualRate, setManualRate] = useState('');

  // BNR exchange rate state
  const [bnrRate, setBnrRate] = useState(null);

  // Helper to detect if invoice has foreign currency (EUR, USD, etc.)
  // Checks both invoice-level moneda field and individual line BT-5 fields
  const hasForeignCurrency = (invoice) => {
    if (!invoice) return false;

    // Check invoice-level currency first (most reliable)
    const invoiceCurrency = (invoice.moneda || 'RON').toUpperCase();
    if (invoiceCurrency !== 'RON' && invoiceCurrency !== 'LEI') {
      return true;
    }

    // Also check individual lines if they have BT-5 set
    const lines = invoice.linii_factura;
    if (lines && lines.length) {
      return lines.some(line => {
        const lineCurrency = (line['BT-5'] || '').toUpperCase();
        return lineCurrency && lineCurrency !== 'RON' && lineCurrency !== 'LEI';
      });
    }

    return false;
  };

  // Helper to detect if value is a CNP (13 digits)
  const isCnp = (value) => {
    if (!value) return false;
    const clean = value.toUpperCase().replace('RO', '').trim();
    return clean.length === 13 && /^\d+$/.test(clean);
  };

  // "azi 02:30" / "ieri 14:05" / "12.07.2026 09:00" for SPV sync moments
  const formatSyncMoment = (iso) => {
    if (!iso) return null;
    const d = new Date(iso);
    if (isNaN(d.getTime())) return null;
    const now = new Date();
    const time = d.toLocaleTimeString('ro-RO', { hour: '2-digit', minute: '2-digit' });
    const yesterday = new Date(now);
    yesterday.setDate(now.getDate() - 1);
    if (d.toDateString() === now.toDateString()) return `azi ${time}`;
    if (d.toDateString() === yesterday.toDateString()) return `ieri ${time}`;
    return `${d.toLocaleDateString('ro-RO')} ${time}`;
  };

  // Explanatory verification status for the invoice number: says exactly what
  // was compared against the SPV series cache and that the final check runs
  // live at send time. Replaces the old, confusing bare "neverificat".
  const formatNumberCheckStatus = (inv) => {
    if (inv.numar_verificat_spv_la) {
      return `✓ verificat direct în ANAF la ${new Date(inv.numar_verificat_spv_la).toLocaleString('ro-RO')}`;
    }
    const chk = inv.numar_check;
    const liveTail = 'la trimitere se va mai verifica live o dată, direct în ANAF';
    if (!chk) {
      return `la trimitere se va verifica live, direct în ANAF`;
    }
    const sync = formatSyncMoment(chk.last_sync_at);
    const syncPart = sync ? ` (sincronizat ${sync})` : '';
    switch (chk.case) {
      case 'fits':
        return `în continuarea seriei din SPV — ultimul număr cunoscut: ${chk.last_known}${syncPart}; ${liveTail}`;
      case 'ahead':
        return `sare peste următorul așteptat (${chk.expected_next})${syncPart} — posibil există facturi emise în afara aplicației; ${liveTail}`;
      case 'behind':
        return `mai mic decât ultimul număr cunoscut din SPV (${chk.last_known})${syncPart} — risc de respingere ANAF; ${liveTail}`;
      case 'unknown':
      default:
        if (chk.last_sync_at) {
          return `fără istoric SPV în ultimele 60 de zile${syncPart}; ${liveTail}`;
        }
        return `seriile SPV nu au fost încă sincronizate pentru acest client; la trimitere se va verifica live, direct în ANAF`;
    }
  };

  // ANAF e-Factura submission hook
  const {
    submitting: anafSubmitting,
    lastError: anafError,
    anafStatus,
    checkAnafReady,
    submitToAnaf,
    clearState: clearAnafState
  } = useAnafSubmit();

  // Chitanta handlers
  const assignChitantaNumber = async () => {
    setAssigningChitanta(true);
    try {
      const res = await apiFetch(`/api/invoices/${id}/assign-chitanta`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' }
      });
      if (!res.ok) {
        const data = await res.json();
        throw new Error(data.message || 'Eroare la atribuirea numărului de chitanță');
      }
      const data = await res.json();
      // Update local state with the new chitanta number
      setEditedInvoice(prev => ({
        ...prev,
        numar_chitanta: data.numar_chitanta
      }));
      setBaseInvoice(prev => ({
        ...prev,
        numar_chitanta: data.numar_chitanta
      }));
      showMessage({ type: 'success', text: `Chitanța a primit numărul ${data.numar_chitanta}` });
    } catch (e) {
      alert(`Eroare: ${e.message}`);
    } finally {
      setAssigningChitanta(false);
    }
  };

  // Create chitanta for non-NUMERAR invoices (changes payment method + assigns number)
  const createChitanta = async () => {
    setCreatingChitanta(true);
    try {
      const res = await apiFetch(`/api/invoices/${id}/create-chitanta`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' }
      });
      if (!res.ok) {
        const data = await res.json();
        throw new Error(data.message || 'Eroare la crearea chitanței');
      }
      const data = await res.json();
      // Update local state with the new chitanta number and payment method
      setEditedInvoice(prev => ({
        ...prev,
        numar_chitanta: data.numar_chitanta,
        modalitate_plata: data.modalitate_plata
      }));
      setBaseInvoice(prev => ({
        ...prev,
        numar_chitanta: data.numar_chitanta,
        modalitate_plata: data.modalitate_plata
      }));
      showMessage({ type: 'success', text: `Chitanța a fost creată cu numărul ${data.numar_chitanta}` });
    } catch (e) {
      alert(`Eroare: ${e.message}`);
    } finally {
      setCreatingChitanta(false);
    }
  };

  // Initialize edited invoice when original invoice loads
  useEffect(() => {
    if (invoice) {
      const normalized = { ...invoice };
      setBaseInvoice(normalized);
      setEditedInvoice({ ...normalized });
    }
  }, [invoice]);

  // Fetch BNR exchange rate for the invoice date (previous day's rate, matching conversion logic)
  useEffect(() => {
    if (!invoice) return;
    const fetchBnrRate = async () => {
      try {
        const invoiceDate = invoice.data_emitere || new Date().toISOString().split('T')[0];
        const response = await apiFetch(`/api/bnr/rate?date=${invoiceDate}`);
        const data = await response.json();
        if (data.rate) {
          setBnrRate(data);
          if (!data.is_default) {
            setManualRate(prev => prev === '' ? String(data.rate) : prev);
          }
        }
      } catch (err) {
        console.error('Error fetching BNR rate:', err);
      }
    };
    fetchBnrRate();
  }, [invoice]);

  // Validation function
  const validateInvoice = (invoiceData) => {
    const errors = {};

    // Validate seller
    if (!invoiceData.seller_snapshot?.nume?.trim()) {
      errors.seller_nume = 'Denumirea emitentului este obligatorie';
    }

    // Validate buyer
    if (!invoiceData.buyer_snapshot?.nume?.trim()) {
      errors.buyer_nume = 'Denumirea beneficiarului este obligatorie';
    }

    // Validate header
    if (!invoiceData.numar_factura?.trim()) {
      errors.numar_factura = 'Numărul facturii este obligatoriu';
    }
    if (!invoiceData.data_emitere) {
      errors.data_emitere = 'Data emiterii este obligatorie';
    }

    // Validate lines
    if (!invoiceData.linii_factura || invoiceData.linii_factura.length === 0) {
      errors.linii_factura = 'Cel puțin o linie de factură este obligatorie';
    } else {
      invoiceData.linii_factura.forEach((line, index) => {
        if (!line['BT-153']?.trim()) {
          errors[`line_${index}_description`] = `Denumirea produsului pe linia ${index + 1} este obligatorie`;
        }
        // EN 16931: cantitatea poate fi negativă (linii de discount); doar 0 este invalid
        const qty = parseFloat(line['BT-129']);
        if (isNaN(qty) || qty === 0) {
          errors[`line_${index}_quantity`] = `Cantitatea pe linia ${index + 1} trebuie să fie diferită de 0`;
        }
        if (!line['BT-146'] || parseFloat(line['BT-146']) <= 0) {
          errors[`line_${index}_price`] = `Prețul unitar pe linia ${index + 1} trebuie să fie mai mare ca 0`;
        }
      });
    }

    return errors;
  };

  const canApproveInvoice = (invoiceData) => {
    // Check 1: All required fields must be filled (no validation errors)
    const hasValidationErrors = Object.keys(validateInvoice(invoiceData || {})).length > 0;

    // Check 2: Invoice status must be GREEN (prefer confidence_flag over stare_validare)
    const effectiveStatus = invoiceData?.confidence_flag;
    const isGreen = effectiveStatus === 'GREEN';

    // Both conditions must be true to enable the button
    return !hasValidationErrors && isGreen;
  };

  const handleEdit = async () => {
    setIsEditing(true);
    setSaveMessage(null);
    setValidationErrors({});

    // Scroll invoice content into view (centered) after a brief delay for UI to update
    setTimeout(() => {
      if (invoiceContentRef.current) {
        invoiceContentRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    }, 100);

    // Fetch end users list for the dropdown
    if (editedInvoice?.id_client) {
      setLoadingEndUsers(true);
      const url = `/api/clients/${editedInvoice.id_client}/end-users-list`;

      try {
        const response = await apiFetch(url);

        if (response.ok) {
          const users = await response.json();
          setEndUsers(users);
        } else {
          const errorText = await response.text();
          console.error('❌ Failed to fetch end users, status:', response.status, errorText);
        }
      } catch (err) {
        console.error('❌ Exception fetching end users:', err);
      } finally {
        setLoadingEndUsers(false);
      }
    } else {
      console.warn('⚠️ No id_client found in editedInvoice, cannot fetch end users');
    }
  };

  const handleCancel = () => {
    setIsEditing(false);
    setEditedInvoice(baseInvoice ? { ...baseInvoice } : null);
    setValidationErrors({});
    setSaveMessage(null);
  };

  const buildSavePayload = (source) => {
    const payload = {
      ...source,
      buyer_snapshot: source?.buyer_snapshot ? { ...source.buyer_snapshot } : {},
      seller_snapshot: source?.seller_snapshot ? { ...source.seller_snapshot } : {},
      linii_factura: (source?.linii_factura || []).map((line) => ({ ...line })),
    };

    if ('pipeline_status' in payload) delete payload.pipeline_status;
    if ('email_hints' in payload) delete payload.email_hints;

    return payload;
  };

  const handleSave = async () => {
    const errors = validateInvoice(editedInvoice);
    setValidationErrors(errors);

    if (Object.keys(errors).length > 0) {
      showMessage({ type: 'error', text: 'Vă rugăm să corectați erorile de validare înainte de salvare.' });
      // Ensure the user sees the message immediately
      try { window.scrollTo({ top: 0, behavior: 'smooth' }); } catch (_) {}
      return;
    }

    // Ensure data_scadenta >= data_emitere
    let invoiceToSave = { ...editedInvoice };
    if (invoiceToSave.data_emitere && invoiceToSave.data_scadenta) {
      if (invoiceToSave.data_scadenta < invoiceToSave.data_emitere) {
        invoiceToSave.data_scadenta = invoiceToSave.data_emitere;
        setEditedInvoice(invoiceToSave);
      }
    }

    setProcessing(true);
    try {
      const payload = buildSavePayload(invoiceToSave);
      const response = await apiFetch(`/api/invoices/${id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      });

      if (!response.ok) {
        // Try to surface server-side validation details
        let serverMsg = 'Eroare la salvarea modificărilor';
        try {
          const payload = await response.clone().json();
          if (payload?.errors && Array.isArray(payload.errors)) {
            serverMsg = payload.errors.join('; ');
          } else if (payload?.message) {
            serverMsg = payload.message;
          }
        } catch (_) {}
        throw new Error(serverMsg);
      }

      // Revalidate invoice to update status based on new data
      try {
        const revalidateResp = await apiFetch(`/api/invoices/${id}/revalidate`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' }
        });
        if (revalidateResp.ok) {
          await revalidateResp.json();
        }
      } catch (revalidateErr) {
        console.warn('Revalidation failed (non-critical):', revalidateErr);
      }

      // Refetch fresh invoice from server to ensure UI mirrors DB state
      try {
        const freshResp = await apiFetch(`/api/invoices/${id}`);
        if (freshResp.ok) {
        const fresh = await freshResp.json();
        const normalizedFresh = { ...fresh };
        setBaseInvoice(normalizedFresh);
        setEditedInvoice(normalizedFresh);
      }
      } catch (_) { /* non-blocking */ }

      setIsEditing(false);
      showMessage({ type: 'success', text: 'Modificările au fost salvate cu succes!' });

    } catch (err) {
      showMessage({ type: 'error', text: `Eroare la salvare: ${err.message}` });
    } finally {
      setProcessing(false);
    }
  };

  const handleApprove = async () => {
    // Ensure all required fields are valid before sending
    const currentErrors = validateInvoice(editedInvoice);
    setValidationErrors(currentErrors);
    if (Object.keys(currentErrors).length > 0) {
      // stay on page and show errors
      try { window.scrollTo({ top: 0, behavior: 'smooth' }); } catch (_) {}
      return;
    }

    // Check ANAF readiness before submission
    const anafReady = checkAnafReady();
    if (!anafReady.ready) {
      showMessage({ type: 'error', text: anafReady.reason });
      return;
    }

    // Real-time pre-submit validation using CURRENT form data (catches last-minute changes)
    try {
      const preValidateResp = await apiFetch(`/api/invoices/${id}/pre-submit-validate`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          data_emitere: editedInvoice.data_emitere,
          numar_factura: editedInvoice.numar_factura
        })
      });

      if (!preValidateResp.ok) {
        const result = await preValidateResp.json();

        // SPV collision: the number already exists at ANAF for this issuer.
        // Show what it is in SPV and offer the proposed next number (one click).
        if (result.spv_collision && result.spv_collision.proposed_next) {
          const c = result.spv_collision;
          const d = c.spv_details || {};
          const detail = d.buyer
            ? `\nÎn SPV: emisă către ${d.buyer}, total ${d.total ?? '?'}, la ${d.date ?? '?'}.`
            : '';
          const syncInfo = c.last_sync_at
            ? `\n(Ultima sincronizare SPV: ${new Date(c.last_sync_at).toLocaleString('ro-RO')})`
            : '';
          const accept = window.confirm(
            `Numărul ${c.number} există deja în SPV.${detail}\n\n` +
            `Folosiți numărul propus ${c.proposed_next}?${syncInfo}`
          );
          if (accept) {
            setEditedInvoice(prev => ({
              ...prev,
              numar_factura: c.proposed_next,
              numar_origine: 'client_document_corrected',
              observatii_validare:
                `${prev.observatii_validare ? prev.observatii_validare + '\n' : ''}` +
                `Număr corectat după verificare SPV: ${c.number} → ${c.proposed_next}.`
            }));
            showMessage({
              type: 'success',
              text: `Număr actualizat la ${c.proposed_next}. Apăsați din nou „Trimite la ANAF" pentru a continua.`
            });
            return;
          }
        }

        const errorText = result.errors ? result.errors.join('\n') : 'Eroare la validarea facturii';

        // Update local state to show RED status and error in interpretabilitate
        setEditedInvoice(prev => ({
          ...prev,
          confidence_flag: 'RED',
          observatii_validare: errorText
        }));

        showMessage({
          type: 'error',
          text: errorText
        });
        return; // Block submission
      }
    } catch (err) {
      showMessage({
        type: 'error',
        text: 'Eroare la validarea facturii înainte de trimitere.'
      });
      return;
    }

    setProcessing(true);
    clearAnafState();

    try {
      // Step 1: Save any pending changes first
      const saveResponse = await apiFetch(`/api/invoices/${id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          ...editedInvoice,
          observatii_validare: (editedInvoice.observatii_validare ? `${editedInvoice.observatii_validare}\n` : '') + 'Se trimite la ANAF...'
        })
      });

      if (!saveResponse.ok) {
        let serverMsg = 'Eroare la salvarea facturii';
        try {
          const payload = await saveResponse.clone().json();
          if (payload?.errors && Array.isArray(payload.errors)) {
            serverMsg = payload.errors.join('; ');
          } else if (payload?.message) {
            serverMsg = payload.message;
          }
        } catch (_) {}
        throw new Error(serverMsg);
      }

      // Step 2: Submit to ANAF e-Factura
      // Pass forceStandardInvoiceCode option if user wants to use 380 instead of 751 for BON_FISCAL
      const result = await submitToAnaf(parseInt(id), {
        forceStandardInvoiceCode: forceStandardInvoiceCode
      });

      if (result.success) {
        // Update local state with ANAF response
        const next = {
          ...editedInvoice,
          stare_trimitere: 'trimisa',
          anaf_upload_id: result.data.uploadId,
          anaf_status: result.data.status,
          observatii_validare: (editedInvoice.observatii_validare ? `${editedInvoice.observatii_validare}\n` : '') +
            `ANAF: Trimisă cu succes. Upload ID: ${result.data.uploadId}. Status: ${result.data.status}`
        };
        setEditedInvoice(next);
        showMessage({
          type: 'success',
          text: `Factură trimisă în ANAF! Upload ID: ${result.data.uploadId}`
        });
      } else {
        throw new Error(result.error || 'Eroare la trimiterea în ANAF');
      }

    } catch (err) {
      showMessage({ type: 'error', text: `Eroare la trimitere: ${err.message}` });
    } finally {
      setProcessing(false);
    }
  };

  // Download XML for non-RON currency invoices (cannot be submitted to ANAF)
  const handleDownloadXml = async () => {
    if (!editedInvoice?.id) return;

    setProcessing(true);
    setSaveMessage(null);

    try {
      const response = await apiFetch(`/api/invoices/${editedInvoice.id}/xml`);

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Eroare la descărcarea XML-ului');
      }

      // Download the XML file
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      const seria = editedInvoice.seria || '';
      const numar = editedInvoice.numar || editedInvoice.numar_factura || editedInvoice.id;
      a.download = `factura_${seria}_${numar}.xml`.replace(/\s+/g, '_');
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);

      showMessage({ type: 'success', text: 'XML descărcat cu succes!' });
    } catch (err) {
      console.error('XML download error:', err);
      showMessage({ type: 'error', text: `Eroare la descărcarea XML: ${err.message}` });
    } finally {
      setProcessing(false);
    }
  };

  const handleDelete = async () => {
    const confirm = window.confirm('Sigur doriți să ștergeți această factură? Acțiunea este ireversibilă.');
    if (!confirm) return;
    setProcessing(true);
    try {
      const res = await apiFetch(`/api/invoices/${id}`, { method: 'DELETE' });
      if (!res.ok) {
        let msg = 'Eroare la ștergerea facturii';
        try { const payload = await res.clone().json(); if (payload?.message) msg = payload.message; } catch (_) {}
        throw new Error(msg);
      }
      // Navigate back to source list
      const from = location.state && location.state.from;
      const clientId = location.state && location.state.clientId;
      const returnState = location.state?.returnState;
      const to = from === 'client' && clientId ? `/clients/${clientId}` : '/validation-hub';

      // Preserve state when navigating back
      const navState = from === 'client'
        ? { from: 'invoice' }
        : returnState
          ? { returnState }
          : undefined;

      navigate(to, { state: navState });
    } catch (e) {
      alert(e.message);
    } finally {
      setProcessing(false);
    }
  };

  // Removed reject action per requirements

  // Recalculate totals on the client from lines for immediate feedback
  const recalcTotalsFromLines = (lines) => {
    let net = 0;
    let vat = 0;
    const updated = (lines || []).map((line) => {
      const qty = parseFloat(line['BT-129'] ?? 0) || 0;
      const price = parseFloat(line['BT-146'] ?? 0) || 0;
      const rawPct = line['BT-152'];
      let pct = 0;
      if (rawPct !== null && rawPct !== undefined && rawPct !== '') {
        const n = parseFloat(rawPct);
        pct = Number.isFinite(n) ? n : 0;
      }
      const rate = pct / 100.0;
      const lineNet = qty * price;
      const lineVat = lineNet * rate;
      net += lineNet;
      vat += lineVat;
      return { ...line, 'BT-131': lineNet };
    });
    return { updatedLines: updated, net, vat, total: net + vat };
  };

  const handleFieldChange = (fieldName, value) => {
    if (fieldName === 'linii_factura') {
      const { updatedLines, net, vat, total } = recalcTotalsFromLines(value);
      setEditedInvoice(prev => ({
        ...prev,
        linii_factura: updatedLines,
        suma_totala_net_linii: net,
        total_net_impozabil: net,
        total_tva: vat,
        total_general: total,
        suma_de_plata: total,
      }));
    } else {
      setEditedInvoice(prev => ({
        ...prev,
        [fieldName]: value
      }));
    }
    // Clear validation error for this field
    if (validationErrors[fieldName]) {
      setValidationErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[fieldName];
        return newErrors;
      });
    }
  };

  const handlePartyChange = (partyType, field, value) => {
    setEditedInvoice(prev => ({
      ...prev,
      [`${partyType}_snapshot`]: {
        ...prev[`${partyType}_snapshot`],
        [field]: value
      }
    }));
    // Clear validation error
    const errorKey = `${partyType}_${field}`;
    if (validationErrors[errorKey]) {
      setValidationErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[errorKey];
        return newErrors;
      });
    }
  };

  // ANAF CUI lookup handler
  const handleAnafLookup = async () => {
    const cui = editedInvoice.buyer_snapshot?.cui?.trim();

    if (!cui) {
      setAnafLookupError('Introduceți CUI-ul pentru a căuta');
      return;
    }

    setAnafLookupLoading(true);
    setAnafLookupError(null);

    try {
      const res = await apiFetch(`/api/anaf/company-lookup?cui=${encodeURIComponent(cui)}`);
      const json = await res.json();

      if (!res.ok || !json.success) {
        throw new Error(json.error || 'Eroare la căutarea în ANAF');
      }

      // ANAF is ground truth - replace ALL fields with ANAF data
      const data = json.data;
      setEditedInvoice(prev => ({
        ...prev,
        buyer_snapshot: {
          ...prev.buyer_snapshot,
          nume: data.nume,
          nr_reg_com: data.nr_reg_com,
          adresa: data.adresa,
          oras: data.oras,
          judet: data.judet,
          tara: data.tara_cod || 'RO',
        }
      }));

      const vatStatus = data.scp_tva ? 'Plătitor TVA' : 'Neplătitor TVA';
      showMessage({ type: 'success', text: `Date completate din ANAF (${vatStatus})` });

    } catch (e) {
      setAnafLookupError(e.message);
    } finally {
      setAnafLookupLoading(false);
    }
  };

  // Currency conversion handler - converts EUR/USD to RON using BNR rate
  const handleCurrencyConvert = async () => {
    if (!editedInvoice.linii_factura?.length) return;

    setCurrencyConvertLoading(true);

    try {
      // Get invoice date for rate lookup (use today if not set)
      const invoiceDate = editedInvoice.data_emitere || new Date().toISOString().split('T')[0];

      // The editable rate field is the source of truth: BNR-prefilled or
      // manually entered. Guardrails: numeric (comma or dot), >0, <=100,
      // max 4 decimals.
      const rateText = String(manualRate).trim().replace(',', '.');
      if (!rateText) {
        throw new Error('Introduceți cursul EUR/RON (BNR indisponibil sau câmp gol)');
      }
      if (!/^\d+(\.\d{1,4})?$/.test(rateText)) {
        throw new Error('Curs invalid: folosiți un număr cu maxim 4 zecimale (ex: 5.2424)');
      }
      const eurRate = parseFloat(rateText);
      if (!(eurRate > 0 && eurRate <= 100)) {
        throw new Error('Curs în afara intervalului rezonabil (0 - 100)');
      }
      const isBnrValue = bnrRate && !bnrRate.is_default && Math.abs(eurRate - bnrRate.rate) < 1e-9;
      const rateDate = isBnrValue ? bnrRate.date : 'curs introdus manual';

      // Get invoice-level currency (lines may not have BT-5 set individually)
      const invoiceCurrency = (editedInvoice.moneda || 'RON').toUpperCase();

      // Convert all foreign currency lines to RON
      const convertedLines = editedInvoice.linii_factura.map(line => {
        // Use line-level BT-5 if set, otherwise fall back to invoice-level currency
        const lineCurrency = (line['BT-5'] || invoiceCurrency).toUpperCase();

        if (lineCurrency === 'EUR') {
          const originalPrice = parseFloat(line['BT-146']) || 0;
          const convertedPrice = Math.round(originalPrice * eurRate * 100) / 100;
          return {
            ...line,
            'BT-146': convertedPrice,
            'BT-5': 'RON',
            '_original_amount': originalPrice,
            '_original_currency': 'EUR'
          };
        }
        // TODO: Add USD conversion if needed
        return line;
      });

      // Recalculate all totals (net, VAT, total) based on converted line prices
      const { updatedLines, net, vat, total } = recalcTotalsFromLines(convertedLines);

      setEditedInvoice(prev => ({
        ...prev,
        linii_factura: updatedLines,
        moneda: 'RON',
        suma_totala_net_linii: net,
        total_net_impozabil: net,
        total_tva: vat,
        total_general: total,
        suma_de_plata: total,
        currency_conversion: {
          rate: eurRate,
          rate_date: rateDate,
          converted: true
        }
      }));

      showMessage({
        type: 'success',
        text: `Convertit în RON la cursul ${eurRate.toFixed(4)} EUR/RON din ${rateDate}`
      });

    } catch (e) {
      showMessage({ type: 'error', text: e.message });
    } finally {
      setCurrencyConvertLoading(false);
    }
  };

  const handleEndUserSelect = async (endUserId) => {
    if (!endUserId) return;

    console.log('🔍 Selected end user ID:', endUserId);

    // Fetch the full end user details
    try {
      const response = await apiFetch(`/api/end-users/${endUserId}`);
      if (response.ok) {
        const data = await response.json();
        console.log('🔍 API response:', data);

        // API returns data nested under 'end_user' key
        const endUserData = data.end_user;
        console.log('🔍 End user data:', endUserData);

        // Map EndUser fields to buyer_snapshot with simple field names
        const buyerSnapshot = {
          nume: endUserData.nume || '',
          cui: endUserData.cui || endUserData.identifier || '',
          adresa: endUserData.adresa || '',
          oras: endUserData.oras || '',
          judet: endUserData.judet || '',
          tara: endUserData.tara_cod || 'RO',
          nr_reg_com: endUserData.nr_reg_com || '',
        };

        // Also store CNP separately if it exists (for persons)
        if (endUserData.cnp) {
          buyerSnapshot.cnp = endUserData.cnp;
        }

        console.log('✅ Created buyer snapshot:', buyerSnapshot);

        setEditedInvoice(prev => ({
          ...prev,
          id_end_user: endUserId,
          buyer_snapshot: buyerSnapshot
        }));

        // Clear buyer validation errors
        setValidationErrors(prev => {
          const newErrors = { ...prev };
          Object.keys(newErrors).forEach(key => {
            if (key.startsWith('buyer_')) {
              delete newErrors[key];
            }
          });
          return newErrors;
        });
      }
    } catch (err) {
      console.error('❌ Failed to fetch end user details:', err);
    }
  };

  if (loading) {
    return (
      <div className="invoice-detail">
        <main className="invoice-detail-main">
          <LoadingState />
        </main>
      </div>
    );
  }

  if (error) {
    return (
      <div className="invoice-detail">
        <main className="invoice-detail-main">
          <ErrorState error={error} />
        </main>
      </div>
    );
  }

  if (!invoice || !editedInvoice) return null;

  // Build navigation context
  const from = location.state?.from;
  const clientId = location.state?.clientId;
  const returnState = location.state?.returnState;
  const backTo = from === 'client' && clientId ? `/clients/${clientId}` : '/validation-hub';
  const backLabel = from === 'client' && clientId ? 'Înapoi la Emitent' : 'Înapoi la Listă';
  const navState = from === 'client'
    ? { from: 'invoice' }
    : returnState
      ? { returnState }
      : undefined;

  // Build breadcrumbs based on navigation context
  const breadcrumbs = from === 'client' && clientId
    ? [
        { label: 'Validare', to: '/validation-hub' },
        { label: 'Emitenți', to: '/clients' },
        { label: editedInvoice.emitent?.nume || 'Emitent', to: `/clients/${clientId}` },
        { label: `Factură #${editedInvoice.numar_factura}`, to: null }
      ]
    : [
        { label: 'Validare', to: '/validation-hub' },
        { label: `Factură #${editedInvoice.numar_factura}`, to: null }
      ];

  return (
    <div className="invoice-detail">
      <header className="invoice-detail-header">
        <div className="invoice-detail-header-content">
          <div className="invoice-detail-nav">
            <Breadcrumbs items={breadcrumbs} />
            <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
              <Link to={backTo} className="back-btn" state={navState}>
                ← {backLabel}
              </Link>
              <div>
                <h1 className="invoice-detail-title">
                  {isEditing ? 'Editare' : 'Revizuire'} Factură #{editedInvoice.numar_factura}
                </h1>
                <p className="invoice-detail-subtitle">
                  Emisă la {editedInvoice.data_emitere} • Validare pentru transmitere în SPV ANAF
                </p>
              </div>
            </div>
          </div>

          {/* Right side: BNR Rate + View Source Email Button */}
          <div style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: '1rem' }}>
            {/* BNR Exchange Rate Display */}
            {bnrRate && (
              <div className="bnr-rate-display" title={`Curs BNR din ${bnrRate.date || 'N/A'} (ziua precedentă)`}>
                <span className="bnr-rate-text">
                  {bnrRate.is_default ? 'curs BNR indisponibil' : `${bnrRate.rate?.toFixed(4)} EUR/RON`}
                </span>
              </div>
            )}

            {/* View Source Email Button */}
            {invoice.gmail_message_id && (
              <Link
                to={`/invoice/${id}/email`}
                className="back-btn"
                style={{ flexShrink: 0 }}
              >
                Vezi Email Sursă
              </Link>
            )}
          </div>
        </div>
      </header>

      <main className="invoice-detail-main">
        <div className="invoice-detail-content" ref={invoiceContentRef}>
          {/* Save message */}
          {saveMessage && (
            <ValidationMessage type={saveMessage.type} message={saveMessage.text} />
          )}

          {/* Status Overview */}
          <StatusBadge
            status={editedInvoice.confidence_flag}
            observations={editedInvoice.observatii_validare}
          />

          {/* Invoice Header Information */}
          <div className={`section-card ${isEditing ? 'section-card-editing' : ''}`}>
            <div className="section-header section-header-blue">
              <h2 className="section-title">Informații Antet Factură</h2>
            </div>
            <div className="section-content">
              <div className="form-grid form-grid-4">
                <div>
                  <FormField
                    label="Număr Factură"
                    value={editedInvoice.numar_factura}
                    isEditing={isEditing}
                    onChange={handleFieldChange}
                    fieldName="numar_factura"
                    mono
                    required
                    error={validationErrors.numar_factura}
                    placeholder="Ex: INV-001"
                  />
                  {editedInvoice.numar_origine && editedInvoice.numar_origine !== 'legacy' && (
                    <div
                      style={{ fontSize: '0.78rem', opacity: 0.75, marginTop: '0.3rem' }}
                      title={editedInvoice.numar_verificat_spv_la
                        ? `Verificat direct în ANAF la ${new Date(editedInvoice.numar_verificat_spv_la).toLocaleString('ro-RO')}`
                        : 'Verificarea finală se face automat, direct în ANAF, în momentul trimiterii'}
                    >
                      {editedInvoice.numar_origine === 'client_document' && 'Sursă: documentul clientului'}
                      {editedInvoice.numar_origine === 'client_document_corrected' && 'Sursă: documentul clientului (corectat după SPV)'}
                      {editedInvoice.numar_origine === 'spv_next' && 'Sursă: următorul după ultima factură cunoscută din SPV'}
                      {' — '}
                      {formatNumberCheckStatus(editedInvoice)}
                    </div>
                  )}
                </div>
                <FormField 
                  label="Data Emiterii" 
                  value={isEditing ? editedInvoice.data_emitere : formatDateRo(editedInvoice.data_emitere)}
                  isEditing={isEditing}
                  onChange={handleFieldChange}
                  fieldName="data_emitere"
                  type="date"
                  required
                  error={validationErrors.data_emitere}
                />
                <FormField 
                  label="Data Scadenței" 
                  value={isEditing ? editedInvoice.data_scadenta : formatDateRo(editedInvoice.data_scadenta)}
                  isEditing={isEditing}
                  onChange={handleFieldChange}
                  fieldName="data_scadenta"
                  type="date"
                />
                <FormField
                  label="Moneda"
                  value={editedInvoice.moneda}
                  isEditing={isEditing}
                  onChange={handleFieldChange}
                  fieldName="moneda"
                  placeholder="RON"
                />
                {/* Currency Conversion Button - show when foreign currency detected and not yet converted */}
                {isEditing && hasForeignCurrency(editedInvoice) && !editedInvoice.currency_conversion?.converted && (
                  <div className="currency-convert-section">
                    <div className="form-field" style={{ marginBottom: '0.4rem' }}>
                      <label className="form-field-label" style={{ fontSize: '0.8rem' }}>Curs EUR/RON</label>
                      <input
                        type="text"
                        inputMode="decimal"
                        className="form-field-input"
                        style={{ maxWidth: '140px', fontFamily: 'Monaco, Menlo, monospace' }}
                        value={manualRate}
                        onChange={(e) => setManualRate(e.target.value)}
                        placeholder={bnrRate?.is_default ? 'BNR indisponibil' : 'ex: 5.2424'}
                      />
                      <div style={{ fontSize: '0.72rem', opacity: 0.65, marginTop: '2px' }}>
                        {bnrRate && !bnrRate.is_default
                          ? `Curs BNR din ${bnrRate.date} — editabil`
                          : 'Curs BNR indisponibil — introduceți manual'}
                      </div>
                    </div>
                    <button
                      type="button"
                      onClick={handleCurrencyConvert}
                      disabled={currencyConvertLoading}
                      className={`currency-convert-btn ${currencyConvertLoading ? 'loading' : ''}`}
                      title="Convertește sumele în EUR/USD la cursul BNR oficial"
                    >
                      {currencyConvertLoading ? 'Se convertește...' : 'Convertește în RON'}
                    </button>
                  </div>
                )}
                {/* Currency Conversion Rate - shown when EUR was converted to RON */}
                {editedInvoice.currency_conversion?.converted && (
                  <div className="form-field currency-conversion-info">
                    <label className="form-field-label">Conversie valutara</label>
                    <div className="form-field-value">
                      {editedInvoice.currency_conversion.rate?.toFixed(4)} EUR/RON
                      <span className="currency-rate-date"> ({editedInvoice.currency_conversion.rate_date})</span>
                    </div>
                    {/* Show original EUR total if lines have _original_amount */}
                    {editedInvoice.linii_factura?.some(l => l._original_amount) && (
                      <div className="currency-original-amount">
                        Original: {editedInvoice.linii_factura
                          .filter(l => l._original_amount)
                          .reduce((sum, l) => sum + (l._original_amount || 0), 0)
                          .toFixed(2)} EUR
                      </div>
                    )}
                  </div>
                )}
                {/* Modalitate Plată */}
                <div className="form-field">
                  <label className="form-field-label">Modalitate Plată</label>
                  {isEditing ? (
                    <select
                      className="form-field-input"
                      value={editedInvoice.modalitate_plata || 'ORDIN DE PLATA'}
                      onChange={(e) => handleFieldChange('modalitate_plata', e.target.value)}
                    >
                      <option value="nespecificata">Nespecificată</option>
                      <option value="NUMERAR">NUMERAR</option>
                      <option value="ORDIN DE PLATA">ORDIN DE PLATĂ</option>
                      <option value="CARD">CARD</option>
                      <option value="BON_FISCAL">BON FISCAL</option>
                      <option value="RAMBURS">RAMBURS</option>
                    </select>
                  ) : (
                    <div className="form-field-value">
                      {editedInvoice.modalitate_plata || 'ORDIN DE PLATA'}
                    </div>
                  )}
                </div>

                {/* Warning banner for BON_FISCAL - Invoice code 751 */}
                {editedInvoice.modalitate_plata === 'BON_FISCAL' && (
                  <div className="invoice-type-warning">
                    <div className="warning-header">
                      <span className="warning-icon">⚠️</span>
                      <strong>Cod factură: {forceStandardInvoiceCode ? '380 (Standard)' : '751 (Bon Fiscal)'}</strong>
                    </div>
                    <p className="warning-text">Această factură va fi transmisă cu codul {forceStandardInvoiceCode ? '380' : '751'} deoarece este achitată cu bon fiscal.</p>
                    <label className="override-checkbox">
                      <input
                        type="checkbox"
                        checked={forceStandardInvoiceCode}
                        onChange={(e) => setForceStandardInvoiceCode(e.target.checked)}
                      />
                      Folosește cod standard (380)
                    </label>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Parties Information */}
          <div className="section-card">
            <div className="section-header">
              <h2 className="section-title">Informații Parteneri</h2>
              <p className="section-subtitle">Date identificare emitent și beneficiar</p>
            </div>
            <div className="section-content">
              <div className="party-container">
                <PartyDetails
                  title="Vânzător (Emitent Factură)"
                  party={editedInvoice.seller_snapshot}
                  type="seller"
                  isEditing={isEditing}
                  onChange={handlePartyChange}
                  errors={validationErrors}
                />

                {/* Buyer selection section */}
                <div className="party-card">
                  {/* Searchable dropdown for selecting buyer (only in edit mode) */}
                  {isEditing && (
                    <div style={{ marginBottom: '1.5rem', padding: '1rem', backgroundColor: '#f4f2ec', borderRadius: '0.5rem', border: '1px solid #e2ded3' }}>
                      <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '600', fontSize: '0.875rem', color: '#4a4335' }}>
                        Selectează Beneficiar din listă
                      </label>
                      <select
                        className="form-field-input"
                        value={editedInvoice.id_end_user || ''}
                        onChange={(e) => handleEndUserSelect(parseInt(e.target.value))}
                        disabled={loadingEndUsers || endUsers.length === 0}
                        style={{ width: '100%', padding: '0.5rem', fontSize: '0.875rem' }}
                      >
                        <option value="">
                          {endUsers.length === 0
                            ? '-- Nu există beneficiari valizi pentru acest emitent --'
                            : '-- Selectează beneficiar existent --'}
                        </option>
                        {endUsers.map(user => (
                          <option key={user.id} value={user.id}>
                            {user.nume}
                          </option>
                        ))}
                      </select>
                      {loadingEndUsers && (
                        <p style={{ marginTop: '0.5rem', fontSize: '0.75rem', color: '#746c5a' }}>
                          Se încarcă lista de beneficiari...
                        </p>
                      )}
                      {!loadingEndUsers && endUsers.length === 0 && (
                        <p style={{ marginTop: '0.5rem', fontSize: '0.75rem', color: '#dc2626' }}>
                          ⚠️ Nu există beneficiari valizi. Creați un beneficiar nou din pagina emitentului.
                        </p>
                      )}
                      {!loadingEndUsers && endUsers.length > 0 && (
                        <p style={{ marginTop: '0.5rem', fontSize: '0.75rem', color: '#746c5a' }}>
                          Selectați un beneficiar existent. Pentru a edita datele, accesați pagina beneficiarului.
                        </p>
                      )}
                    </div>
                  )}

                  {/* Beneficiary fields are always read-only - edit via beneficiary management page */}
                  <PartyDetails
                    title="Cumpărător (Beneficiar)"
                    party={editedInvoice.buyer_snapshot}
                    type="buyer"
                    isEditing={isEditing}
                    onChange={handlePartyChange}
                    errors={validationErrors}
                    onAnafLookup={handleAnafLookup}
                    anafLookupLoading={anafLookupLoading}
                    anafLookupError={anafLookupError}
                    isCnpValue={isCnp(editedInvoice.buyer_snapshot?.cui)}
                  />
                </div>
              </div>
            </div>
          </div>
          
          {/* Invoice Lines */}
          <InvoiceLinesTable
            lines={editedInvoice.linii_factura}
            isEditing={isEditing}
            onChange={handleFieldChange}
            errors={validationErrors}
            sellerCui={editedInvoice.seller_snapshot?.cui || ''}
            clientCategorieTVA={editedInvoice.client?.categorieTVA || null}
            clientTvaPercent={editedInvoice.client_tva_procent ?? 19}
          />
          
          {/* Financial Totals - Auto-calculated, read-only */}
          <div className="section-card">
            <div className="section-header">
              <h2 className="section-title">Totaluri Financiare</h2>
              <p className="section-subtitle">
                {isEditing ? 'Calculare automată pe baza liniilor de factură' : 'Sumar financiar factură'}
              </p>
            </div>
            <div className="section-content">
              <div className="totals-container">
                <div className="totals-box">
                  <div className="total-row">
                    <span className="total-label">Subtotal Net (fără TVA)</span>
                    <span className="total-value">
                      {parseFloat(editedInvoice.total_net_impozabil || 0).toLocaleString('ro-RO', {
                        minimumFractionDigits: 2,
                        maximumFractionDigits: 2
                      })} {editedInvoice.moneda}
                    </span>
                  </div>
                  
                  <div className="total-row">
                    <span className="total-label">Total TVA</span>
                    <span className="total-value">
                      {parseFloat(editedInvoice.total_tva || 0).toLocaleString('ro-RO', {
                        minimumFractionDigits: 2,
                        maximumFractionDigits: 2
                      })} {editedInvoice.moneda}
                    </span>
                  </div>
                  
                  <div className="total-row total-row-final">
                    <span className="total-label total-label-final">Total General</span>
                    <span className="total-value total-value-final">
                      {parseFloat(editedInvoice.total_general || 0).toLocaleString('ro-RO', {
                        minimumFractionDigits: 2,
                        maximumFractionDigits: 2
                      })} {editedInvoice.moneda}
                    </span>
                  </div>
                  
                  <div className="total-row total-row-highlight">
                    <span className="total-label total-label-highlight">Sumă de Plată</span>
                    <span className="total-value total-value-highlight">
                      {parseFloat(editedInvoice.suma_de_plata || 0).toLocaleString('ro-RO', {
                        minimumFractionDigits: 2,
                        maximumFractionDigits: 2
                      })} {editedInvoice.moneda}
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Chitanta Section - Always visible */}
          <div className="section-card">
            <div className="section-header section-header-blue" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
              <div>
                <h2 className="section-title">Chitanță</h2>
                <p className="section-subtitle">Document pentru plată în numerar</p>
              </div>
              {/* Show "Creează Chitanță" button when not NUMERAR */}
              {String(editedInvoice.modalitate_plata || '').toUpperCase() !== 'NUMERAR' && (
                <button
                  type="button"
                  className="action-btn action-btn-approve"
                  onClick={createChitanta}
                  disabled={creatingChitanta}
                >
                  {creatingChitanta ? 'Se creează...' : 'Creează Chitanță'}
                </button>
              )}
              {/* Show "Atribuie Număr" button when NUMERAR but no number */}
              {String(editedInvoice.modalitate_plata || '').toUpperCase() === 'NUMERAR' &&
               editedInvoice.numar_chitanta == null &&
               !(isEditing && baseInvoice?.numar_chitanta != null) && (
                <button
                  type="button"
                  className="action-btn action-btn-approve"
                  onClick={assignChitantaNumber}
                  disabled={assigningChitanta}
                >
                  {assigningChitanta ? 'Se atribuie...' : 'Atribuie Număr Chitanță'}
                </button>
              )}
            </div>
            <div className="section-content">
              {/* Case 1: NOT NUMERAR payment - show message */}
              {String(editedInvoice.modalitate_plata || '').toUpperCase() !== 'NUMERAR' ? (
                <div style={{ color: '#746c5a', fontSize: '0.9375rem', textAlign: 'center', padding: '1rem' }}>
                  Chitanța este disponibilă doar pentru plăți în numerar.
                </div>
              ) : (
                /* Case 2: NUMERAR payment */
                (editedInvoice.numar_chitanta != null || (isEditing && baseInvoice?.numar_chitanta != null)) ? (
                  /* Render chitanta document inline - matching PDF exactly */
                  <div style={{
                    border: '1px solid #000',
                    padding: '16px 20px',
                    fontFamily: 'Arial, Helvetica, sans-serif',
                    fontSize: '12px',
                    lineHeight: '1.4',
                    backgroundColor: '#fdfcf9',
                    color: '#000'
                  }}>
                    {/* Header row - Seller info left, CHITANTA right */}
                    <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '12px' }}>
                      {/* Left side - Seller info */}
                      <div style={{ flex: 1 }}>
                        <div style={{ fontWeight: 'bold', fontSize: '13px', marginBottom: '2px' }}>
                          Unitatea {editedInvoice.seller_snapshot?.nume || ''}
                        </div>
                        <div style={{ marginBottom: '2px' }}>
                          CIF {editedInvoice.seller_snapshot?.cui || ''}
                        </div>
                        <div style={{ marginBottom: '2px' }}>
                          Adresa {[
                            editedInvoice.seller_snapshot?.oras?.toUpperCase(),
                            editedInvoice.seller_snapshot?.adresa
                          ].filter(Boolean).join(', ')}
                        </div>
                        {editedInvoice.seller_snapshot?.numeBanca && (
                          <div style={{ marginBottom: '2px' }}>
                            Banca {editedInvoice.seller_snapshot.numeBanca}
                          </div>
                        )}
                        {editedInvoice.seller_snapshot?.iban && (
                          <div>
                            IBAN {Array.isArray(editedInvoice.seller_snapshot.iban)
                              ? editedInvoice.seller_snapshot.iban[0]
                              : editedInvoice.seller_snapshot.iban}
                          </div>
                        )}
                      </div>
                      {/* Right side - CHITANTA title and details */}
                      <div style={{ textAlign: 'right', minWidth: '160px' }}>
                        <div style={{ fontWeight: 'bold', fontSize: '20px', marginBottom: '4px' }}>CHITANTA</div>
                        <div style={{ fontWeight: 'bold', fontSize: '13px' }}>
                          Numar{' '}
                          {isEditing ? (
                            <input
                              type="text"
                              inputMode="numeric"
                              pattern="[0-9]*"
                              style={{
                                width: '50px',
                                border: '1px solid #000',
                                padding: '2px 4px',
                                fontSize: '13px',
                                fontWeight: 'bold',
                                textAlign: 'center',
                                fontFamily: 'inherit'
                              }}
                              value={editedInvoice.numar_chitanta ?? ''}
                              onChange={(e) => {
                                const val = e.target.value;
                                if (val === '' || /^\d+$/.test(val)) {
                                  setEditedInvoice(prev => ({
                                    ...prev,
                                    numar_chitanta: val === '' ? null : parseInt(val, 10)
                                  }));
                                }
                              }}
                            />
                          ) : (
                            <span>{editedInvoice.numar_chitanta}</span>
                          )}
                        </div>
                        <div style={{ fontWeight: 'bold', fontSize: '13px' }}>
                          Data {editedInvoice.data_emitere ?
                            new Date(editedInvoice.data_emitere).toLocaleDateString('ro-RO', {
                              day: '2-digit',
                              month: '2-digit',
                              year: 'numeric'
                            }) : ''}
                        </div>
                      </div>
                    </div>

                    {/* Separator line */}
                    <div style={{ borderTop: '1px solid #000', margin: '10px 0' }} />

                    {/* Am primit de la row */}
                    <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '4px' }}>
                      <div style={{ fontWeight: 'bold' }}>
                        Am primit de la{' '}
                        <span style={{ fontWeight: 'bold' }}>{editedInvoice.buyer_snapshot?.nume || ''}</span>
                      </div>
                      <div>
                        {editedInvoice.buyer_snapshot?.cui && `CIF ${editedInvoice.buyer_snapshot.cui}`}
                        {editedInvoice.buyer_snapshot?.cui && editedInvoice.buyer_snapshot?.nr_reg_com && '    '}
                        {editedInvoice.buyer_snapshot?.nr_reg_com && `RC ${editedInvoice.buyer_snapshot.nr_reg_com}`}
                      </div>
                    </div>

                    {/* Adresa buyer */}
                    <div style={{ marginBottom: '8px' }}>
                      Adresa {[
                        editedInvoice.buyer_snapshot?.judet && `Judetul ${editedInvoice.buyer_snapshot.judet.toUpperCase()}`,
                        editedInvoice.buyer_snapshot?.oras?.toUpperCase(),
                        editedInvoice.buyer_snapshot?.adresa
                      ].filter(Boolean).join(', ')}
                    </div>

                    {/* Suma */}
                    <div style={{ marginBottom: '4px' }}>
                      <span style={{ fontWeight: 'bold' }}>Suma de {Number(editedInvoice.total_general || 0).toLocaleString('ro-RO', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
                      {' '}adica {numberToWordsRo(Number(editedInvoice.total_general || 0), editedInvoice.moneda || 'RON')}
                    </div>

                    {/* Reprezentind */}
                    <div style={{ marginBottom: '12px' }}>
                      <span style={{ fontWeight: 'bold' }}>Reprezentind</span>{' '}c\v fact. {editedInvoice.numar_factura || ''}
                    </div>

                    {/* Casier */}
                    <div style={{ textAlign: 'right', fontStyle: 'italic' }}>
                      Casier,
                    </div>
                  </div>
                ) : (
                  /* NUMERAR but no chitanta number assigned yet */
                  <div style={{ color: '#92400e', fontSize: '0.9375rem', textAlign: 'center', padding: '1rem' }}>
                    Chitanța nu are încă un număr atribuit.
                  </div>
                )
              )}
            </div>
          </div>

          {/* Action Buttons - Only show if invoice is NOT sent to SPV */}
          {editedInvoice.stare_trimitere !== 'trimisa' && (
            <div className="section-card">
              <div className="section-content">
                <div className="actions-container">
                  <div className="actions-info">
                    <h3>Acțiuni de Validare</h3>
                    <p>
                      {isEditing
                        ? 'Salvați modificările înainte de aprobare sau anulați pentru a reveni la datele originale'
                        : 'Alegeți acțiunea pentru această factură în procesul SPV ANAF'
                      }
                    </p>
                  </div>
                  <div className="actions-buttons">
                    {isEditing ? (
                      <>
                        <button
                          type="button"
                          onClick={handleCancel}
                          className="action-btn action-btn-cancel"
                        >
                          Anulează
                        </button>
                        <button
                          type="button"
                          onClick={handleSave}
                          disabled={processing}
                          className={`action-btn ${processing ? 'action-btn-processing' : 'action-btn-save'}`}
                        >
                          {processing ? 'Se salvează...' : 'Salvează Modificările'}
                        </button>
                      </>
                    ) : (
                      <>
                        <button
                          type="button"
                          onClick={handleEdit}
                          className="action-btn action-btn-edit"
                        >
                          Editează Date
                        </button>
                        {canApproveInvoice(editedInvoice) && (
                          <>
                            {/* Show different buttons based on currency */}
                            {isRonCurrency(editedInvoice.moneda) ? (
                              <>
                                {/* ANAF Status indicator - only for RON invoices */}
                                <AnafStatusButton />
                                {/* Submit to ANAF button - only for RON currency */}
                                <button
                                  type="button"
                                  onClick={handleApprove}
                                  disabled={processing || anafSubmitting || !anafStatus.hasToken || anafStatus.isExpired}
                                  className={`action-btn action-btn-approve ${(processing || anafSubmitting) ? 'action-btn-processing' : ''}`}
                                  title={
                                    !anafStatus.hasToken ? 'Autentificați-vă în ANAF pentru a trimite' :
                                    anafStatus.isExpired ? 'Token ANAF expirat - reautentificați-vă' :
                                    'Trimite factura în ANAF'
                                  }
                                >
                                  {(processing || anafSubmitting) ? 'Se trimite la ANAF...' : 'Trimite ANAF'}
                                </button>
                              </>
                            ) : (
                              /* Download XML button - for non-RON currencies (USD, EUR, etc.) */
                              <button
                                type="button"
                                onClick={handleDownloadXml}
                                disabled={processing}
                                className={`action-btn action-btn-download ${processing ? 'action-btn-processing' : ''}`}
                                title="Descarcă XML-ul factură (monedă diferită de RON)"
                              >
                                {processing ? 'Se generează...' : 'Generează XML'}
                              </button>
                            )}
                          </>
                        )}
                        {/* Show warning if can approve but ANAF not ready - only for RON */}
                        {canApproveInvoice(editedInvoice) && isRonCurrency(editedInvoice.moneda) && !anafStatus.hasToken && !anafStatus.loading && (
                          <span style={{ color: '#f59e0b', fontSize: '0.85rem', marginLeft: '0.5rem' }}>
                            (Autentificare ANAF necesară)
                          </span>
                        )}
                        {/* Show ANAF error if any */}
                        {anafError && (
                          <span style={{ color: '#dc2626', fontSize: '0.85rem', marginLeft: '0.5rem' }}>
                            {anafError}
                          </span>
                        )}
                        <button
                          type="button"
                          onClick={handleDelete}
                          disabled={processing}
                          className={`action-btn action-btn-reject ${processing ? 'action-btn-processing' : ''}`}
                        >
                          Șterge Factura
                        </button>
                      </>
                    )}
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* ANAF Status Section for sent invoices - simplified view */}
          {editedInvoice.stare_trimitere === 'trimisa' && (
            <div className="section-card">
              <div className="section-content">
                <div style={{ padding: '1rem' }}>
                  {/* Header */}
                  <h3 style={{ color: '#16a34a', marginBottom: '1rem', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                    <span>✓</span> Factură Trimisă în ANAF
                  </h3>

                  {/* Simplified Status Grid - only Upload ID and Status */}
                  <div style={{
                    display: 'flex',
                    gap: '2rem',
                    marginBottom: '1.5rem',
                    padding: '1rem',
                    backgroundColor: 'rgba(211, 192, 132, 0.05)',
                    borderRadius: '8px',
                    border: '1px solid rgba(211, 192, 132, 0.15)'
                  }}>
                    {/* Upload ID */}
                    <div>
                      <div style={{ fontSize: '0.8rem', color: '#746c5a', marginBottom: '0.25rem' }}>Upload ID</div>
                      <div style={{ fontWeight: 600, color: '#059669', fontFamily: 'monospace' }}>
                        {editedInvoice.anaf_upload_id || '-'}
                      </div>
                    </div>

                    {/* Status (normalized) */}
                    <div>
                      <div style={{ fontSize: '0.8rem', color: '#746c5a', marginBottom: '0.25rem' }}>Status</div>
                      <div style={{
                        fontWeight: 600,
                        color: editedInvoice.anaf_status === 'accepted' ? '#16a34a' :
                               editedInvoice.anaf_status === 'rejected' ? '#dc2626' :
                               editedInvoice.anaf_status === 'error' ? '#dc2626' :
                               editedInvoice.anaf_status === 'processing' ? '#f59e0b' : '#746c5a'
                      }}>
                        {editedInvoice.anaf_status === 'accepted' ? 'Acceptat' :
                         editedInvoice.anaf_status === 'rejected' ? 'Respins' :
                         editedInvoice.anaf_status === 'error' ? 'Eroare' :
                         editedInvoice.anaf_status === 'processing' ? 'În procesare' :
                         editedInvoice.anaf_status || 'necunoscut'}
                      </div>
                    </div>
                  </div>

                  {/* Error Message Display - when status is error */}
                  {editedInvoice.anaf_status === 'error' && (editedInvoice.anaf_response_message || editedInvoice.anaf_errors_json) && (
                    <div style={{
                      marginBottom: '1rem',
                      padding: '0.75rem 1rem',
                      backgroundColor: 'rgba(220, 38, 38, 0.1)',
                      borderRadius: '8px',
                      border: '1px solid rgba(220, 38, 38, 0.3)'
                    }}>
                      <div style={{ fontSize: '0.8rem', color: '#dc2626', marginBottom: '0.25rem', fontWeight: 600 }}>
                        Mesaj eroare ANAF:
                      </div>
                      <div style={{ color: '#dc2626', fontSize: '0.9rem' }}>
                        {editedInvoice.anaf_response_message ||
                         (editedInvoice.anaf_errors_json && Array.isArray(editedInvoice.anaf_errors_json)
                           ? editedInvoice.anaf_errors_json.join('; ')
                           : 'Eroare necunoscută')}
                      </div>
                    </div>
                  )}

                  {/* Single Action Button - ANAF Status Link */}
                  <Link to="/anaf-invoices" className="anaf-status-link">
                    Vezi situație ANAF
                    <span className="anaf-status-link-arrow">→</span>
                  </Link>
                </div>
              </div>
            </div>
          )}

          {/* Learning-loop panels: AI-vs-final corrections + ANAF submission audit */}
          <ExtractionDiffPanel invoiceId={id} />
          <SubmissionAuditPanel invoiceId={id} />
        </div>
      </main>
    </div>
  );
}
